<template>
  <div class="facade">
    <div v-if="!cookieRef && !$preview" class="facade__wrapper">
      <div class="facade__content">
        <h2 class="facade__title">Je hebt onze {{cookieType}} cookies (nog) niet geaccepteerd.</h2>
        <p class="facade__text">Voor een volledig werkende website accepteer je hier de {{cookieType}} cookies.</p>
        <AppButton title="Cookie instellingen" @click.prevent="openCookieSettings" />
      </div>
    </div>
    <div v-else class="facade">
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { TCookie } from '~~/types/local_types';

const emit = defineEmits(['allowed']);
const { cookieDebug } = useRuntimeConfig().public;
const { $preview } = useNuxtApp()
const cookieRef = ref(false);

interface IProps {
  cookieType?: TCookie;
}

const props = withDefaults(defineProps<IProps>(), {
  cookieType: 'overige',
});

const cookieMappings = {
  social: 'Cookie_Category_Social',
  overige: 'Cookie_Category_Miscellaneous',
  noCookie: 'Cookie_Category_No_Cookie',
};

const { sld, top } = useUrl();

const cookie = useCookie(cookieMappings[props.cookieType], {
  domain: `.${sld}.${top}`,
  default: () => false,
});

watch(
  cookie,
  () => {
    cookieRef.value = cookie.value || false;
  },
  { immediate: true },
);

onMounted(() => {
  checkCookie();
  emit('allowed', cookieRef.value);
});

onUpdated(() => {
  checkCookie();
});

const checkCookie = () => {
  if (cookieDebug || props.cookieType === 'noCookie') {
    cookieRef.value = true;
  }
};

checkCookie();

const openCookieSettings = () => {
  if (import.meta.client) {
    if (window && window.ccm) {
      window.ccm.showCookieMessage();
    }
  }
};

</script>

<style scoped lang="postcss">
.facade {
  display: contents;

  &__wrapper {
    grid-column: 1 / -1;

    @media (--sm) {
      grid-column: span 12 / span 12;
    }

    @media (--md) {
      grid-column: span 10 / span 10;
      grid-column-start: 2;
    }

    @media (--lg) {
      grid-column: span 8 / span 8;
      grid-column-start: 3;
    }

    @media (--xl) {
      grid-column: span 6 / span 6;
      grid-column-start: 4;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    @mixin app-blue-border-block;
  }

  &__title {
    color: var(--color-blue-aqua);
    @mixin heading-h4-wide;
  }

  &__text {
    color: var(--color-blue-aqua);
  }
}

</style>
